/**
 * THE shared model for "an AI process is running and here is what it is doing".
 *
 * Three generations of progress UI grew three partial copies of this logic —
 * `Research/activity.ts`, `Review/playbookExtraction.ts`, and the flat reducer
 * inside `hooks/useProcessingPubSub.ts` - each tolerant of a slightly different
 * subset of the engine's key spellings. This is the union of the three, and the
 * rules it enforces are the ones the other two were written to fix:
 *
 *  1. **Nothing is pre-drawn.** A stage exists once the engine has mentioned
 *     it, never before. A pipeline that skips a step therefore never shows one
 *     sitting unfinished - which is exactly what the matter tracker did on
 *     every single matter creation, because `_build_active_steps()` decides the
 *     real step list at runtime from the user's AI toggles.
 *  2. **No invented percentages.** `percent` is what the engine said, or
 *     `null`. `null` renders as a moving sliver; it never renders as a number.
 *  3. **Never silent.** A `connecting` pseudo-stage stands in from the instant
 *     the run is live until the first real event lands.
 *  4. **Two text tiers per stage.** `message` is the engine's own line;
 *     `narration` is the quieter aside underneath it. `narration` is an
 *     OPTIONAL key inside the event's `data` - the contract playbook
 *     extraction already ships - so a flow whose engine never sends one
 *     renders exactly as it would without this field.
 *  5. **Four terminal states.** `completed`, `completed_with_errors` (the run
 *     finished but some items were unreadable), `failed`, `cancelled`.
 */

// ─── Types ───────────────────────────────────────────────────────────────────

export type AIRunStatus =
  | 'idle'
  | 'connecting'
  | 'running'
  | 'completed'
  | 'completed_with_errors'
  | 'failed'
  | 'cancelled';

/**
 * `pending` is for a step list the ENGINE announced up front (matter analysis
 * is told the run's real steps in its opening event). It is not a licence to
 * pre-draw: a stage still only exists because something reported it.
 */
export type AIStageStatus = 'pending' | 'active' | 'done' | 'failed' | 'skipped';

/** A count the engine reported about a stage - never derived here. */
export interface AIStageFact {
  label: string;
  value: string | number;
}

export interface AIStage {
  /** Raw engine step key. An unknown key still renders, with a derived label. */
  key: string;
  /** Untranslated label - run through `t()` at render time. */
  label: string;
  status: AIStageStatus;
  /** The engine's own words, verbatim. */
  message?: string;
  /** The optional quieter second line. Absent for engines that don't send one. */
  narration?: string;
  error?: string;
  startedAt: number;
  facts?: AIStageFact[];
}

export type AISubItemStatus = 'pending' | 'active' | 'completed' | 'failed';

/** One file / document / row inside a stage. */
export interface AISubItem {
  id: string;
  name: string;
  status: AISubItemStatus;
  /** 0–100 when the engine reported one for this item, else null. */
  percent: number | null;
  message?: string;
}

export interface AIFailedItem {
  name: string;
  reason?: string;
}

export interface AIRun {
  status: AIRunStatus;
  stages: AIStage[];
  subItems: AISubItem[];
  /** Items the run could not process. Populated by the completion event. */
  failedItems: AIFailedItem[];
  /** Engine-reported ONLY. `null` means "unknown", and renders as such. */
  percent: number | null;
  /** 1-based, engine-reported or derived from stages actually opened. */
  stepCurrent: number;
  /** Engine-reported total; 0 while the pipeline hasn't announced its length. */
  stepTotal: number;
  message: string | null;
  narration: string | null;
  error: string | null;
  startedAt: number | null;
  elapsedSeconds: number;
}

export const initialAIRun: AIRun = {
  status: 'idle',
  stages: [],
  subItems: [],
  failedItems: [],
  percent: null,
  stepCurrent: 0,
  stepTotal: 0,
  message: null,
  narration: null,
  error: null,
  startedAt: null,
  elapsedSeconds: 0,
};

/**
 * Open a run. `subItemNames` seeds the per-item list from what the user
 * submitted, so the rows are on screen from the first frame rather than
 * appearing one at a time as the engine happens to name each of them.
 */
export function beginAIRun(
  startedAt: number = Date.now(),
  subItemNames: string[] = [],
): AIRun {
  return {
    ...initialAIRun,
    status: 'connecting',
    startedAt,
    subItems: subItemNames.map((name) => ({
      id: name,
      name,
      status: 'pending' as const,
      percent: null,
    })),
  };
}

// ─── Stage labels & the connecting stand-in ──────────────────────────────────

/**
 * Labels for keys seen across the flows today. Anything absent is title-cased
 * from its own key, so a stage the engine adds tomorrow renders readably
 * instead of being dropped or mislabelled.
 */
export const AI_STAGE_LABELS: Record<string, string> = {
  connecting: 'Connecting',

  // research
  intent: 'Reading the question',
  orchestration: 'Choosing sources',
  retrieval: 'Searching sources',
  evaluation: 'Weighing findings',
  synthesis: 'Writing the answer',
  verification: 'Checking citations',

  // matter pipeline
  collecting_context: 'Collecting Context',
  organizing_data: 'Organizing Data',
  optimizing_data: 'Optimizing Data',
  analysing_legal_sources: 'Analysing Legal Sources',
  legal_sources: 'Analysing Legal Sources',
  generating_relevant_tasks: 'Generating Relevant Tasks',
  final_matter_evaluation: 'Final Matter Evaluation',

  // index / data source / table creation
  quota: 'Checking quota',
  create: 'Creating',
  upload: 'Uploading files',
  index: 'Building the index',
  setup_index: 'Building the index',
  datasource: 'Connecting the data source',
  setup_datasource: 'Connecting the data source',
  indexer: 'Configuring the indexer',
  setup_indexer: 'Configuring the indexer',
  run: 'Running',
  wait: 'Waiting for the engine',
  commit: 'Saving',

  // import
  analyzing_files: 'Analyzing files',
  extracting_client_data: 'Extracting contact data',
  adding_clients: 'Adding contacts',
};

/** Normalize any engine spelling of a step into a stable key. */
export function aiStageKey(raw: string): string {
  return raw.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
}

/**
 * A step id turned into something a lawyer can read.
 *
 * A name the engine already wrote in prose ("Analyzing clause 4 of 12") passes
 * straight through - only bare ids are mapped, or else sentence-cased
 * ("LEGAL_SOURCES" never reaches the screen as shouted capitals).
 */
export function aiStageLabel(raw: string): string {
  const key = aiStageKey(raw);
  if (AI_STAGE_LABELS[key]) return AI_STAGE_LABELS[key];
  const trimmed = raw.trim();
  if (/\s/.test(trimmed)) return trimmed;
  if (!key) return trimmed;
  const words = key.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * The stage shown the moment a run is live, before any engine event.
 *
 * A submit should feel answered instantly. Waiting for the first real event
 * left a beat of nothing - G1 showed an empty rail or a bare centered loader —
 * so the connection itself is the first stage.
 */
export const CONNECTING_STAGE: AIStage = {
  key: 'connecting',
  label: 'Connecting',
  status: 'active',
  startedAt: 0,
};

/** The stages to paint: the engine's, or the standing-in connecting stage. */
export function displayStages(run: AIRun): AIStage[] {
  if (run.stages.length > 0) return run.stages;
  return isRunLive(run) ? [CONNECTING_STAGE] : [];
}

export function isRunLive(run: AIRun): boolean {
  return run.status === 'connecting' || run.status === 'running';
}

export function isRunFinished(run: AIRun): boolean {
  return (
    run.status === 'completed' ||
    run.status === 'completed_with_errors' ||
    run.status === 'failed' ||
    run.status === 'cancelled'
  );
}

/** Bookkeeping events that are not something to show as a stage of their own. */
const HIDDEN_STAGE_KEYS = new Set([
  'started', 'start', 'completed', 'complete', 'failed', 'welcome', 'system',
]);

export function isVisibleStage(key: string): boolean {
  return !!key && !HIDDEN_STAGE_KEYS.has(key);
}

// ─── Event normalization ─────────────────────────────────────────────────────

export interface NormalizedAIProgress {
  kind: 'progress';
  entityUuid: string | null;
  stageKey: string | null;
  stageLabel: string | null;
  stageStatus: AIStageStatus;
  stepIndex: number | null;
  stepTotal: number | null;
  percent: number | null;
  message: string | null;
  narration: string | null;
  facts: AIStageFact[];
  subItems: AISubItem[];
}

export interface NormalizedAICompletion {
  kind: 'completed';
  entityUuid: string | null;
  message: string | null;
  failedItems: AIFailedItem[];
}

export interface NormalizedAIFailure {
  kind: 'failed';
  entityUuid: string | null;
  error: string;
  failedItems: AIFailedItem[];
}

export interface NormalizedAICancellation {
  kind: 'cancelled';
  entityUuid: string | null;
  message: string | null;
}

export type NormalizedAIEvent =
  | NormalizedAIProgress
  | NormalizedAICompletion
  | NormalizedAIFailure
  | NormalizedAICancellation
  | { kind: 'ignore' };

const str = (value: any): string | null =>
  typeof value === 'string' && value.trim() ? value.trim() : null;

const clampPercent = (value: any): number | null => {
  const n = typeof value === 'string' ? Number(value) : value;
  if (typeof n !== 'number' || !Number.isFinite(n)) return null;
  return Math.min(100, Math.max(0, Math.round(n)));
};

const num = (value: any): number | null => {
  const n = typeof value === 'string' ? Number(value) : value;
  return typeof n === 'number' && Number.isFinite(n) ? n : null;
};

/**
 * Every spelling of "which entity is this about" seen across the flows. The
 * `ai_processing` hub is shared, so a wildcard listener sees everybody's
 * traffic and the id is what tells them apart.
 */
const ENTITY_UUID_KEYS = [
  'entity_uuid', 'entityUuid',
  'matter_uuid', 'matterUuid', 'MatterUUID',
  'template_uuid', 'templateUuid',
  'datasource_uuid', 'dataSourceUuid', 'datasourceUuid',
  'table_uuid', 'tableUuid',
  'review_uuid', 'reviewUuid',
  'playbook_uuid', 'playbookUuid',
  'index_uuid', 'indexUuid',
];

export function aiEventEntityUuid(data: Record<string, any>): string | null {
  for (const key of ENTITY_UUID_KEYS) {
    const value = str(data[key]);
    if (value) return value;
  }
  return null;
}

/** `failed_documents` arrives as bare names or as objects carrying a reason. */
export function normalizeFailedItems(raw: any): AIFailedItem[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((entry): AIFailedItem | null => {
      if (typeof entry === 'string') {
        return str(entry) ? { name: entry.trim() } : null;
      }
      if (!entry || typeof entry !== 'object') return null;
      const name =
        str(entry.original_filename) ||
        str(entry.file_name) ||
        str(entry.filename) ||
        str(entry.name);
      if (!name) return null;
      const reason = str(entry.reason) || str(entry.error) || str(entry.message);
      return reason ? { name, reason } : { name };
    })
    .filter((d): d is AIFailedItem => d !== null);
}

function normalizeFacts(raw: any): AIStageFact[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((entry): AIStageFact | null => {
      if (!entry || typeof entry !== 'object') return null;
      const label = str(entry.label) || str(entry.name) || str(entry.key);
      if (!label) return null;
      const value = entry.value;
      if (value === undefined || value === null) return null;
      return { label, value: typeof value === 'number' ? value : String(value) };
    })
    .filter((f): f is AIStageFact => f !== null);
}

const SUB_ITEM_STATUS: Record<string, AISubItemStatus> = {
  completed: 'completed',
  complete: 'completed',
  done: 'completed',
  failed: 'failed',
  error: 'failed',
  pending: 'pending',
  queued: 'pending',
  waiting: 'pending',
};

/**
 * Per-item rows, in either shape the engines use: an explicit array, or "the
 * run is on this one file right now".
 */
export function normalizeSubItems(data: Record<string, any>): AISubItem[] {
  const list = data.files || data.documents || data.file_progress || data.items;
  if (Array.isArray(list)) {
    return list
      .map((entry: any): AISubItem | null => {
        if (typeof entry === 'string') {
          return str(entry)
            ? { id: entry.trim(), name: entry.trim(), percent: null, status: 'pending' }
            : null;
        }
        if (!entry || typeof entry !== 'object') return null;
        const name =
          str(entry.original_filename) ||
          str(entry.file_name) ||
          str(entry.filename) ||
          str(entry.name);
        if (!name) return null;
        const rawStatus = String(entry.status || '').toLowerCase();
        return {
          id: str(entry.id) || name,
          name,
          percent: clampPercent(entry.percent ?? entry.progress_percent),
          status: SUB_ITEM_STATUS[rawStatus] ?? 'active',
          ...(str(entry.message) ? { message: str(entry.message) as string } : {}),
        };
      })
      .filter((f): f is AISubItem => f !== null);
  }

  const current =
    str(data.current_file) ||
    str(data.file_name) ||
    str(data.filename) ||
    str(data.document_name);
  if (!current) return [];
  return [
    {
      id: current,
      name: current,
      percent: clampPercent(
        data.file_progress_percent ?? data.file_percent ?? data.document_percent,
      ),
      status: 'active',
    },
  ];
}

export interface NormalizeAIEventOptions {
  /**
   * When set, an event carrying a DIFFERENT entity id is ignored. Events with
   * no id at all still pass - several emitters omit it on generic frames.
   */
  entityUuid?: string | null;
  /** Extra event-name prefixes belonging to other features on the shared hub. */
  foreignEventPrefixes?: string[];
}

/**
 * Flatten one PubSub envelope into something the reducer can act on.
 *
 * Tolerant by design: `step_name`/`stepName`/`step`, `progress_percent`/
 * `percent`, `step_total`/`total_steps`, `narration`/`narration_text`/
 * `narrationText` are all the same field wearing different hats depending on
 * which emitter produced the frame.
 */
export function normalizeAIEvent(
  raw: any,
  eventName?: string,
  options: NormalizeAIEventOptions = {},
): NormalizedAIEvent {
  const envelope = Array.isArray(raw) ? raw[0] : raw;
  if (!envelope || typeof envelope !== 'object') return { kind: 'ignore' };

  const inner = envelope.data && typeof envelope.data === 'object' ? envelope.data : {};
  const data: Record<string, any> = { ...envelope, ...inner };

  const entityUuid = aiEventEntityUuid(data);
  if (options.entityUuid && entityUuid && entityUuid !== options.entityUuid) {
    return { kind: 'ignore' };
  }

  const name = String(
    eventName || data.event_name || data.eventType || data.event_type || '',
  ).toLowerCase();
  if (
    options.foreignEventPrefixes?.length &&
    options.foreignEventPrefixes.some((prefix) => name.startsWith(prefix.toLowerCase()))
  ) {
    return { kind: 'ignore' };
  }

  const subType = String(data.sub_type || data.subType || '').toLowerCase();
  const status = String(data.status || '').toLowerCase();
  const message = str(data.message);
  const narration =
    str(data.narration) || str(data.narration_text) || str(data.narrationText);

  // Hub-level connection chatter carries no meaning about the run.
  if (
    !message &&
    (subType === 'connected' || subType === 'disconnected' || subType === 'system')
  ) {
    return { kind: 'ignore' };
  }

  if (status === 'cancelled' || status === 'canceled' || subType === 'cancelled') {
    return { kind: 'cancelled', entityUuid, message };
  }

  const isFailure =
    status === 'failed' ||
    subType === 'failed' ||
    subType === 'error' ||
    (!!data.error && subType !== 'step_failed');

  if (isFailure) {
    return {
      kind: 'failed',
      entityUuid,
      error: str(data.error) || message || 'Processing failed',
      failedItems: normalizeFailedItems(data.failed_documents || data.failed_items),
    };
  }

  const isCompletion =
    status === 'completed' ||
    subType === 'completed' ||
    subType === 'complete' ||
    name === 'ai_processing_complete';

  if (isCompletion) {
    return {
      kind: 'completed',
      entityUuid,
      message,
      failedItems: normalizeFailedItems(data.failed_documents || data.failed_items),
    };
  }

  const rawStage = str(data.step_name) || str(data.stepName) || str(data.step);
  const stageKey = rawStage ? aiStageKey(rawStage) : null;

  // The pipeline's generic opener says nothing about this run.
  if (subType === 'welcome' || (stageKey && !isVisibleStage(stageKey))) {
    return { kind: 'ignore' };
  }

  const percent = clampPercent(data.progress_percent ?? data.percent);
  const subItems = normalizeSubItems(data);
  const facts = normalizeFacts(data.facts);

  // A frame with nothing to say about a stage, an item, or a number moves no
  // pixel - drop it rather than re-rendering the panel.
  if (!stageKey && !subItems.length && percent === null && !message && !narration) {
    return { kind: 'ignore' };
  }

  return {
    kind: 'progress',
    entityUuid,
    stageKey,
    stageLabel: rawStage ? aiStageLabel(rawStage) : null,
    stageStatus:
      subType === 'step_completed'
        ? 'done'
        : subType === 'step_failed'
          ? 'failed'
          : subType === 'step_skipped'
            ? 'skipped'
            : 'active',
    stepIndex: num(data.step_index ?? data.stepIndex),
    stepTotal: num(data.step_total ?? data.total_steps ?? data.totalSteps),
    percent,
    message,
    narration,
    facts,
    subItems,
  };
}

// ─── Reducer ─────────────────────────────────────────────────────────────────

function mergeSubItems(existing: AISubItem[], incoming: AISubItem[]): AISubItem[] {
  if (!incoming.length) return existing;
  const merged = [...existing];
  incoming.forEach((row) => {
    const index = merged.findIndex((f) => f.id === row.id || f.name === row.name);
    if (index === -1) {
      merged.push(row);
      return;
    }
    merged[index] = {
      ...merged[index],
      ...row,
      // An item already at 100 must not slide backwards on a late generic frame.
      percent: row.percent ?? merged[index].percent,
      message: row.message ?? merged[index].message,
    };
  });
  return merged;
}

function closeOpenStages(stages: AIStage[], status: AIStageStatus = 'done'): AIStage[] {
  return stages.map((s) => (s.status === 'active' ? { ...s, status } : s));
}

/**
 * Fold one normalized event into the run.
 *
 * A stage is APPENDED the first time its key is seen and closes whatever was
 * running - the pipeline is sequential and does not always send an explicit
 * completion for the step it just left. Later events for the same key update
 * it in place. Order is arrival order: the pipeline decides the shape of its
 * own progress, and this never guesses at it.
 */
export function reduceAIEvent(
  run: AIRun,
  event: NormalizedAIEvent,
  now: number = Date.now(),
): AIRun {
  switch (event.kind) {
    case 'ignore':
      return run;

    case 'completed': {
      const failedNames = new Set(event.failedItems.map((d) => d.name));
      return {
        ...run,
        status: event.failedItems.length ? 'completed_with_errors' : 'completed',
        stages: closeOpenStages(run.stages),
        percent: 100,
        subItems: run.subItems.map((f) =>
          failedNames.has(f.name)
            ? { ...f, status: 'failed' as const }
            : { ...f, status: 'completed' as const, percent: 100 },
        ),
        failedItems: event.failedItems,
        message: event.message ?? run.message,
        // The run is over: a mid-flight aside about what it was doing is stale.
        narration: null,
        stepCurrent: Math.max(run.stepTotal, run.stages.length),
        error: null,
      };
    }

    case 'failed':
      return {
        ...run,
        status: 'failed',
        stages: closeOpenStages(run.stages, 'failed').map((s) =>
          s.status === 'failed' && !s.error ? { ...s, error: event.error } : s,
        ),
        failedItems: event.failedItems.length ? event.failedItems : run.failedItems,
        narration: null,
        error: event.error,
      };

    case 'cancelled':
      return {
        ...run,
        status: 'cancelled',
        stages: closeOpenStages(run.stages, 'skipped'),
        narration: null,
        message: event.message ?? run.message,
      };

    case 'progress': {
      let stages = run.stages;

      if (event.stageKey) {
        const index = stages.findIndex((s) => s.key === event.stageKey);
        const patch = {
          ...(event.message ? { message: event.message } : {}),
          ...(event.narration ? { narration: event.narration } : {}),
          ...(event.facts.length ? { facts: event.facts } : {}),
        };
        if (index === -1) {
          stages = [
            ...closeOpenStages(stages),
            {
              key: event.stageKey,
              label: event.stageLabel || aiStageLabel(event.stageKey),
              status: event.stageStatus,
              startedAt: now,
              ...patch,
            },
          ];
        } else {
          stages = stages.map((s, i) =>
            i === index
              ? {
                  ...s,
                  label: event.stageLabel || s.label,
                  status: event.stageStatus,
                  ...patch,
                }
              : s,
          );
        }
      }

      const stepTotal = event.stepTotal ?? run.stepTotal;
      const openedStages = stages.length;

      return {
        ...run,
        status: 'running',
        stages,
        subItems: mergeSubItems(run.subItems, event.subItems),
        // Engine-reported only. A frame that carries no number leaves the last
        // one standing rather than inventing a replacement for it.
        percent: event.percent ?? run.percent,
        stepTotal,
        stepCurrent: Math.max(
          run.stepCurrent,
          event.stepIndex !== null ? event.stepIndex : 0,
          openedStages,
        ),
        message: event.message ?? run.message,
        narration: event.narration ?? run.narration,
        startedAt: run.startedAt ?? now,
      };
    }

    default:
      return run;
  }
}

/** Fold a raw PubSub envelope straight into the run. */
export function applyAIEvent(
  run: AIRun,
  raw: any,
  eventName?: string,
  options?: NormalizeAIEventOptions,
): AIRun {
  return reduceAIEvent(run, normalizeAIEvent(raw, eventName, options));
}

// ─── Legacy adapters ─────────────────────────────────────────────────────────

/**
 * The `{ step, status, messages[] }` shape the G1 trackers pass around, mapped
 * onto the shared model.
 *
 * These callers already hold their steps in component or Redux state fed by
 * their own PubSub handlers, so the adapter is what lets them render the
 * branded panel without each one being rewritten down to its socket. As those
 * handlers move to `applyAIEvent`, the adapters go away.
 */
export interface LegacyStep {
  step: string;
  status: 'Pending' | 'In Progress' | 'Completed' | 'Failed' | 'Skipped';
  messages?: string[];
  narration?: string;
}

const LEGACY_STATUS: Record<string, AIStageStatus> = {
  Pending: 'pending',
  'In Progress': 'active',
  Completed: 'done',
  Failed: 'failed',
  Skipped: 'skipped',
};

export function stagesFromLegacySteps(steps: LegacyStep[]): AIStage[] {
  return steps
    .filter((s) => s && typeof s.step === 'string' && isVisibleStage(aiStageKey(s.step)))
    .map((s, i) => {
      const messages = (s.messages || []).filter((m) => typeof m === 'string' && m.trim());
      return {
        key: aiStageKey(s.step) || `step_${i}`,
        label: aiStageLabel(s.step),
        status: LEGACY_STATUS[s.status] ?? 'active',
        startedAt: 0,
        ...(messages.length ? { message: messages[messages.length - 1] } : {}),
        ...(s.narration ? { narration: s.narration } : {}),
      };
    });
}
